import config from 'config';
import { compact, Dictionary } from 'lodash';
import { AbstractOutdoorCampaignFormModel } from './OutdoorCampaignFormModel';
import { LimitationData } from 'core/limitation/Limitation';
import {
  LimitationConfig,
  RtbCampaignBasicFormModel,
  RtbCampaignBasicFormModelConstructorParams
} from './RtbCampaignBasicFormModel';
import { AdType, RtbCampaignPlanType } from 'core/rtbCampaign/RtbCampaign';
import { L1ObjectObjective } from 'core/l1Object/L1Object';
import { L2ObjectOptimizationGoal } from 'core/l2Object/L2Object';
import {
  CampaignAdTypeSetting,
  ChannelGroupSetting
} from 'core/channelGroup/ChannelGroupSettings';
import { ADDONFEATURE, perChannelCampaignKey } from 'core/agency/AddonFeature';
import {
  DEFAULT_INVENTORY,
  LimitationInventorySettings
} from 'containers/Limitations/LimitationSetting/limitationConfig/limitationSettingsType';
import { OPERATE } from 'enum/Operate';
import { defaultItemSetting } from 'components/CampaignLimitation/SelectItemComponent';
import { CustomGeofenceComponent } from 'components/CampaignLimitation/CustomGeofenceComponent';
import { getCustomGeofenceLabel } from 'utils/LimitationUtil';
import i18n from 'i18n';
import { ReverseGeocodeFn } from 'hooks/GoogleMapsPlatform/useGeocoder';
import { deriveAdministrativeAreaFromGeofences } from 'core/rtbCampaign/MovableOutdoorAreaDerivation';

export interface DynamicMovableOutdoorFormModel extends RtbCampaignBasicFormModel {
  readonly canEditBudgetPlan: boolean;
  readonly canEditPriceModel: boolean;
  /**
   * Pre-submit transform: recomputes the `administrativeArea` projection from
   * the customGeofence pin set and injects it. Returns the enriched
   * limitations + an optional `failureMessage` that aborts submit when set.
   * (Load-time strip is handled in RtbCampaignManager — RKB-2541 §9.3a.)
   */
  transformLimitationsForSubmit(
    limitations: LimitationData
  ): Promise<MovableOutdoorSubmitTransform>;
  setReverseGeocode(fn: ReverseGeocodeFn | undefined): void;
}

export type MovableOutdoorSubmitTransform = {
  limitations: LimitationData;
  failureMessage?: string;
};

export abstract class AbstractDynamicMovableOutdoorFormModel
  extends AbstractOutdoorCampaignFormModel
  implements DynamicMovableOutdoorFormModel
{
  adTypeSetting: CampaignAdTypeSetting | undefined;
  private reverseGeocode?: ReverseGeocodeFn;

  constructor(
    protected channelGroupSetting: ChannelGroupSetting,
    actionType: string,
    ...args: RtbCampaignBasicFormModelConstructorParams
  ) {
    super(actionType, ...args);
  }

  override get limitationConfig(): LimitationConfig {
    return {
      defaultLimitationToShow: {
        name: 'customGeofence',
        operate: OPERATE.INCLUDE
      },
      supportAgeGenderLimitation: false,
      supportAudienceEstimation: false,
      requireIncludeLimitation: false,
      operationSet: {
        need: [OPERATE.INCLUDE],
        notNeed: [OPERATE.EXCLUDE],
        other: []
      }
    };
  }

  override get campaignAdType(): AdType {
    return (this.adTypeSetting?.name || 'DYNAMIC_MOVABLE_OUTDOOR') as AdType;
  }

  get mapsApiKey(): string {
    return config.googleMapsApiKey;
  }

  override getRtbOptionsMap(): {
    [objective: string]: Dictionary<L2ObjectOptimizationGoal[] | undefined>;
  } {
    // MOVABLE_OUTDOOR supports FCPM / FCPV / RS only — FWCPM and FACPM are
    // excluded per Notion spec ("拿掉 固定式 wCPM 和 固定式 aCPM").
    const pricingModelOptimizeModel = {
      [RtbCampaignPlanType.FCPM]: L2ObjectOptimizationGoal.IMPRESSIONS,
      [RtbCampaignPlanType.FCPV]: L2ObjectOptimizationGoal.VIDEO_VIEWS
    };
    const optionsMap = {
      [L1ObjectObjective.AWARENESS]: {}
    };
    const addonMap = {
      [RtbCampaignPlanType.RS]: ADDONFEATURE.CAMPAIGN.REVENUE_SHARING,
      [RtbCampaignPlanType.FCPM]: ADDONFEATURE.CAMPAIGN.FIXED_CPM,
      [RtbCampaignPlanType.FCPV]: ADDONFEATURE.CAMPAIGN.FIXED_CPV
    };
    const pricingModels = (this.adTypeSetting?.pricingModel || []).filter(
      (model) =>
        addonMap[model]
          ? this.addonFeatureManager.isFeatureEnable(
              perChannelCampaignKey(
                this.l1Object.channelGroupId,
                addonMap[model]
              )
            )
          : false
    );
    const optimizeModels = compact(
      pricingModels.map((model) => pricingModelOptimizeModel[model])
    );
    pricingModels.forEach((pricingModel) => {
      if (pricingModel === RtbCampaignPlanType.RS || this.optimizeAddonEnable) {
        optionsMap[L1ObjectObjective.AWARENESS][pricingModel] = optimizeModels;
      } else {
        const optimizeModel =
          pricingModelOptimizeModel[pricingModel as RtbCampaignPlanType];
        if (optimizeModel) {
          optionsMap[L1ObjectObjective.AWARENESS][pricingModel] = [
            optimizeModel
          ];
        }
      }
    });
    return optionsMap;
  }

  setReverseGeocode = (fn: ReverseGeocodeFn | undefined) => {
    this.reverseGeocode = fn;
  };

  async transformLimitationsForSubmit(
    limitations: LimitationData
  ): Promise<MovableOutdoorSubmitTransform> {
    if (!this.reverseGeocode) {
      return {
        limitations,
        failureMessage: i18n.t(
          'campaign.errors.movableOutdoorAreaDerivationFailed',
          { coordinates: '' }
        )
      };
    }
    const { limitations: enriched, failedCoordinates } =
      await deriveAdministrativeAreaFromGeofences(
        limitations,
        this.reverseGeocode
      );
    if (failedCoordinates.length > 0) {
      const details = failedCoordinates
        .map(({ lat, lng }) => `(${lat}, ${lng})`)
        .join(', ');
      return {
        limitations,
        failureMessage: i18n.t(
          'campaign.errors.movableOutdoorAreaDerivationFailed',
          { coordinates: details }
        )
      };
    }
    return { limitations: enriched };
  }

  override getLimitationInventorySetting(_1: {
    [key: string]: string[];
  }): LimitationInventorySettings[] {
    // MOVABLE_OUTDOOR limitation inventory per Notion (RKB-2541 §1 Final Spec):
    //   - 限制流量 / 排除指定流量 → 經緯度鎖定 → customGeofence (INCLUDE / EXCLUDE)
    //   - 天氣鎖定 → rendered separately by RtbCampaignSetupStep when the
    //     channelGroup's supportWeatherTargeting addon is on; not an entry here.
    return [
      {
        name: DEFAULT_INVENTORY
      },
      {
        name: 'customGeofence',
        title: 'limitation.labels.customGeofence',
        singleSelect: false,
        ignoreValidateOption: true,
        supportOperates: [OPERATE.INCLUDE, OPERATE.EXCLUDE],
        requiredOperate: [OPERATE.INCLUDE],
        itemSetting: defaultItemSetting(),
        searchPlaceholder: '',
        ignoreAddonFeature: true,
        formatter: (value: string | number) => {
          return getCustomGeofenceLabel(value.toString());
        },
        component: CustomGeofenceComponent
      }
    ];
  }
}

export class EditDynamicMovableOutdoorFormModel extends AbstractDynamicMovableOutdoorFormModel {
  constructor(
    channelGroupSetting: ChannelGroupSetting,
    ...args: RtbCampaignBasicFormModelConstructorParams
  ) {
    super(channelGroupSetting, 'edit', ...args);
  }
  get canEditBudgetPlan(): boolean {
    return false;
  }
  get canEditPriceModel(): boolean {
    return false;
  }
  canEditOptimize(): boolean {
    return false;
  }
}

export class CreateDynamicMovableOutdoorFormModel extends AbstractDynamicMovableOutdoorFormModel {
  constructor(
    channelGroupSetting: ChannelGroupSetting,
    ...args: RtbCampaignBasicFormModelConstructorParams
  ) {
    super(channelGroupSetting, 'create', ...args);
  }
  get canEditBudgetPlan(): boolean {
    return true;
  }
  get canEditPriceModel(): boolean {
    return true;
  }
}
